import cx from "classnames";
import React from "react";

import { MorePosts, MorePostsPlaceholder } from "@/mk2/apps/forum/components/MorePosts";
import SearchPanel from "@/mk2/apps/forum/components/SearchPanel";
import { UnsatisfiedWithResultsTryForum } from "@/mk2/apps/forum/components/UnsatisfiedWithResultsTryForum";
import { LinkedReviewArticlesPanel } from "@/mk2/apps/forum/containers/PostDetail/LinkedReviewArticlesPanel";
import { PREGNANCY_WEEKS_URL } from "@/mk2/apps/forum/containers/PostDetail/PostDetail";
import { ARTICLE_IDS_TO_HIDE_WHEN_PREGNANCY_NEWSLETTER_IS_SHOWN } from "@/mk2/apps/forum/containers/PostDetail/PostDetail.constants";
import { removeLinesWithLinksToArticle } from "@/mk2/apps/forum/containers/PostDetail/PostDetail.helpers";
import { PostDetailViewType, Props } from "@/mk2/apps/forum/containers/PostDetail/PostDetailPage";
import { RelatedWikiArticlesPanel } from "@/mk2/apps/forum/containers/PostDetail/RelatedWikiArticlesPanel";
import { forumSearchUrl } from "@/mk2/apps/forum/urls";
import { BrandDaysPosts } from "@/mk2/apps/home/components/BrandDaysPosts";
import { pregnancyCalculatorUrl } from "@/mk2/apps/wiki/urls";
import { Link } from "@/mk2/components/Link";
import { Panel } from "@/mk2/components/Panel";
import { PanelHeading } from "@/mk2/components/PanelHeading";
import { RenderAST } from "@/mk2/components/RenderAST";
import { StickyBlock } from "@/mk2/components/StickyBlock";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { PregnancyNewsletter } from "@/mk2/containers/PregnancyNewsletter/PregnancyNewsletter";
import { ShowPhotosAndVideos } from "@/mk2/helpers/article_ast/filters";
import { cacheLast } from "@/mk2/helpers/cache";
import { url } from "@/mk2/helpers/urls";
import {
    FORUM_RECENT_POSTS,
    FORUM_SIMILAR_DISCUSSIONS,
    PREGNANCY_NEWSLETTER,
    PREGNANCY_NEWSLETTER_DESCRIPTION,
    PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR,
    PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_01,
    PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_AUTH,
    PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_AUTH_01,
    PREGNANCY_WEEKS_BOX_01,
    PREGNANCY_WEEKS_BOX_02,
    RELATED_DISCUSSIONS_IN_FORUM,
    YOUTUBE_MK_TITLE,
} from "@/mk/autogenerated/translation";
import { hasAds, isAuthenticated } from "@/mk/bazaar/common/userUtils";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import stylesPostDetail from "../../containers/PostDetail/PostDetail.mscss";
import stylesRelated from "../../containers/PostDetail/PostDetailRelated.mscss";
import styles from "./PostDetailRight.mscss";

const STICKY_BLOCK_1_HEIGHT = 2000;
const STICKY_BLOCK_2_HEIGHT = 2000;

export class PostDetailRight extends React.PureComponent<Props> {
    private trackEventPropsCache = cacheLast();

    public render() {
        const { isMobile, similarPosts, recentPosts, loadingState } = this.props;

        return isMobile ? null : (
            <LoadingSwitch
                loadingState={loadingState}
                onLoad={this.load}
                onRenderInit={this.renderInit}
                onRenderSuccess={this.renderSuccess}
                onRenderFailure={this.renderFailure}
                onFailureToast={this.failureToast}
                hasData={
                    (recentPosts && recentPosts.length > 0) ||
                    (similarPosts && similarPosts.length > 0)
                }
            />
        );
    }

    private load = () => {
        /* nothing to do */
    };

    private renderInit = () => {
        const { viewType, requestUser } = this.props;
        const isAskAnExpert = viewType === PostDetailViewType.AskAnExpert;
        const isContest = viewType === PostDetailViewType.Contest;

        return (
            <StickyBlock stickyHeight={STICKY_BLOCK_1_HEIGHT}>
                {this.renderSearchBox()}
                {!isContest && !isAskAnExpert && (
                    <MorePostsPlaceholder
                        key="similar"
                        className={stylesRelated.PostDetailRelated__box}
                        title={FORUM_SIMILAR_DISCUSSIONS}
                    />
                )}
                {!isContest && !isAskAnExpert && (
                    <MorePostsPlaceholder
                        key="youtube"
                        className={stylesRelated.PostDetailRelated__box}
                        title={YOUTUBE_MK_TITLE}
                    />
                )}
                {!isContest && isAskAnExpert && (
                    <MorePostsPlaceholder
                        key="recent"
                        className={stylesRelated.PostDetailRelated__box}
                        title={FORUM_RECENT_POSTS}
                    />
                )}
                {!isContest && isAskAnExpert && (
                    <MorePostsPlaceholder
                        key="related"
                        className={stylesRelated.PostDetailRelated__box}
                        title={RELATED_DISCUSSIONS_IN_FORUM}
                    />
                )}
                {!isAskAnExpert && (
                    <UnsatisfiedWithResultsTryForum
                        requestUser={requestUser}
                        className={stylesRelated.PostDetailRelated__box}
                    />
                )}
            </StickyBlock>
        );
    };

    private renderSuccess = () => {
        const {
            isMobile,
            location,
            requestUser,
            recentPosts,
            post,
            similarPosts,
            brandDaysCampaign,
            brandDaysPosts,
            viewType,
            postCanHavePregnancyNewsletter,
            userCanHavePregnancyNewsletter,
            backlinksBox,
            comments,
            linkedReviewArticles,
            relatedWikiArticles,
        } = this.props;

        if (!post) {
            return null;
        }

        const isAskAnExpert = viewType === PostDetailViewType.AskAnExpert;
        const isContest = viewType === PostDetailViewType.Contest;
        const postHasAds = !post.deletedAt && !post.isNoAds && !post.topic.isCommercial;
        const showBrandDays =
            !!brandDaysCampaign && brandDaysPosts.length > 0 && hasAds(requestUser);

        const pregnancyNewsletterDiv =
            !isMobile && postCanHavePregnancyNewsletter && userCanHavePregnancyNewsletter ? (
                <Panel className={stylesPostDetail.PostDetail__pregnancyNewsletter}>
                    <PanelHeading title={PREGNANCY_NEWSLETTER} />
                    <p>{PREGNANCY_NEWSLETTER_DESCRIPTION}</p>
                    <div
                        className={cx(
                            stylesPostDetail.PostDetail__pregnancyNewsletter__image,
                            stylesPostDetail[
                                `PostDetail__pregnancyNewsletter__image--${ACTIVE_SERVER_ID}`
                            ],
                        )}
                    />
                    <PregnancyNewsletter
                        formNameKey={`forum-post-${post.slug}-right`}
                        noBorder
                        extraEventProps={{
                            subscribe_source: "tehu_nl_widget_forum",
                            subscribe_source_app: "forum",
                            subscribe_source_detail: `forum/${post.topic.slug}/${post.slug}`,
                        }}
                        noPregnancyCalculatorLink
                    />
                </Panel>
            ) : null;

        const linkToPregnancyCalculator01: string = isAuthenticated(requestUser)
            ? PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_AUTH_01
            : PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_01;
        const linkToPregnancyCalculator02: string = isAuthenticated(requestUser)
            ? PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_AUTH
            : PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR;

        const pregnancyWeekBox = !isMobile && postCanHavePregnancyNewsletter && (
            <Panel className={stylesPostDetail.PostDetail__pregnancyWeeksBox}>
                <p className={stylesPostDetail.PostDetail__pregnancyWeeksBox__p}>
                    {PREGNANCY_WEEKS_BOX_01}{" "}
                    <Link to={PREGNANCY_WEEKS_URL[ACTIVE_SERVER_ID]}>{PREGNANCY_WEEKS_BOX_02}</Link>
                    {". "}
                    {linkToPregnancyCalculator01}{" "}
                    <Link to={pregnancyCalculatorUrl}>{linkToPregnancyCalculator02}</Link>
                    {"."}
                </p>
            </Panel>
        );

        const modifiedBacklinksBox =
            postCanHavePregnancyNewsletter && userCanHavePregnancyNewsletter
                ? backlinksBox &&
                  removeLinesWithLinksToArticle(
                      backlinksBox,
                      ARTICLE_IDS_TO_HIDE_WHEN_PREGNANCY_NEWSLETTER_IS_SHOWN,
                  )
                : backlinksBox;

        const recentPostsFiltered = recentPosts.filter((obj) => obj !== post);

        return (
            <>
                <StickyBlock stickyHeight={STICKY_BLOCK_1_HEIGHT}>
                    {this.renderSearchBox()}

                    {showBrandDays && (
                        <BrandDaysPosts
                            className={styles.PostDetailRight__brandDaysPosts}
                            brandDaysCampaign={brandDaysCampaign}
                            brandDaysPosts={brandDaysPosts}
                        />
                    )}

                    {pregnancyNewsletterDiv}
                    {pregnancyWeekBox}

                    {!isContest && postHasAds && comments?.length >= 5 && (
                        <AdSlot
                            key="advertisement1"
                            zone="Forum - Top 02 - Desktop"
                            location={location}
                            marginTopWithAd={10}
                            marginBottomWithAd={10}
                        />
                    )}

                    {!isContest && !isAskAnExpert && (
                        <MorePosts
                            key="similar"
                            className={stylesRelated.PostDetailRelated__box}
                            posts={similarPosts.slice(0, 6)}
                            title={FORUM_SIMILAR_DISCUSSIONS}
                            headingLevel={2}
                            trackEventProps={
                                post &&
                                this.trackEventPropsCache(
                                    {
                                        name: "forum_related_click",
                                        props: {
                                            post_slug: post.slug,
                                            placement: "desktop_sidebar",
                                        },
                                    },
                                    post.slug,
                                    "similar",
                                )
                            }
                        />
                    )}
                    {!isContest && isAskAnExpert && (
                        <MorePosts
                            key="recent"
                            className={stylesRelated.PostDetailRelated__box}
                            posts={recentPostsFiltered}
                            showComments={false}
                            title={FORUM_RECENT_POSTS}
                            headingLevel={2}
                            trackEventProps={
                                post &&
                                this.trackEventPropsCache(
                                    {
                                        name: "counselling_recent_posts_click",
                                        props: {
                                            post_slug: post.slug,
                                            topic_slug: post.topic.slug,
                                            category_slug: post.topic.category.slug,
                                            post_url: location.pathname,
                                        },
                                    },
                                    location.pathname,
                                    "recent",
                                )
                            }
                        />
                    )}
                    {!isContest && isAskAnExpert && (
                        <MorePosts
                            key="related"
                            className={stylesRelated.PostDetailRelated__box}
                            posts={similarPosts}
                            title={RELATED_DISCUSSIONS_IN_FORUM}
                            headingLevel={2}
                            trackEventProps={
                                post &&
                                this.trackEventPropsCache(
                                    {
                                        name: "counselling_more_similar_posts_click",
                                        props: {
                                            post_slug: post.slug,
                                            topic_slug: post.topic.slug,
                                            category_slug: post.topic.category.slug,
                                            post_url: location.pathname,
                                        },
                                    },
                                    location.pathname,
                                    "related",
                                )
                            }
                        />
                    )}

                    <LinkedReviewArticlesPanel
                        isContest={isContest}
                        linkedReviewArticles={linkedReviewArticles}
                    />

                    <RelatedWikiArticlesPanel
                        isContest={isContest}
                        relatedWikiArticles={relatedWikiArticles}
                    />

                    {modifiedBacklinksBox && !isContest && !isAskAnExpert && (
                        <Panel className={stylesRelated.PostDetailRelated__backlinksBox}>
                            <RenderAST
                                contentAST={modifiedBacklinksBox.contentAST}
                                linkedObjects={
                                    modifiedBacklinksBox.contentASTAdditionalData.linkedObjects
                                }
                                showPhotosAndVideos={ShowPhotosAndVideos.ALL}
                                renderSpecialWikiLinks={false}
                                customStyles={{
                                    paragraph: stylesRelated.PostDetailRelated__backlinksBox__p,
                                    unorderedlist:
                                        stylesRelated.PostDetailRelated__backlinksBox__ul,
                                }}
                            />
                        </Panel>
                    )}
                    {/* {!isContest && (
                        <AdSlot
                            key="advertisement2"
                            zone="Forum - Top 03 - Desktop"
                            location={location}
                            marginBottomWithAd={10}
                        />
                    )} */}
                </StickyBlock>

                <StickyBlock stickyHeight={STICKY_BLOCK_2_HEIGHT}>
                    {!isContest && !isAskAnExpert && (
                        <UnsatisfiedWithResultsTryForum
                            requestUser={requestUser}
                            className={stylesRelated.PostDetailRelated__box}
                        />
                    )}
                    {post && !post.deletedAt && !post.isNoAds && !post.topic.isCommercial && (
                        <AdSlot
                            key="mid-01-ad"
                            zone="Forum - Mid 01 - Desktop"
                            location={location}
                        />
                    )}
                </StickyBlock>
            </>
        );
    };

    private renderFailure = () => null;

    private failureToast = () => {
        /* nothing to do */
    };

    private renderSearchBox = () => {
        const { viewType } = this.props;

        if (viewType === PostDetailViewType.Contest) {
            return null;
        }

        return (
            <SearchPanel
                key="search"
                onSubmit={this.onSearch}
                className={styles.PostDetailRight__searchPanel}
            />
        );
    };

    private onSearch = (query: string) => {
        this.props.routeNavigate(url(forumSearchUrl, {}, { q: query }));
    };
}
